import { createSelector } from "reselect";
import get from "lodash/get";
import each from "lodash/each";
import sortBy from "lodash/sortBy";
import findIndex from "lodash/findIndex";
import orderBy from "lodash/orderBy";
import filter from "lodash/filter";
import find from "lodash/find";
import { isCompetingInLift } from "./util/lifterHelper";
import { federationConfigs } from "./util/federations";
import {
  Attempt,
  AttemptNumber,
  EligibleRecordWithWeight,
  LiftName,
  LifterAttempts,
  LiftingOrder,
  Meet,
  Platform,
  ReactRouterWithRouterProps,
  ReduxState,
  RefPosition,
} from "types";

export const getScreenMedia = (state: ReduxState) => state.screenInfo.media;

export const getMeets = (state: ReduxState) => get(state, "meets");
export const getMeetMetaData = (state: ReduxState) =>
  get(state, "meetMetaData");

export const getCurrentMeetId = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => props.match.params.meetId;

const fallbackMeet: Meet = {
  _id: "DEFAULT",
  lifters: {},
  divisions: {},
  platforms: {},
  calculatedData: {},
  entryConfig: { _id: "DEFAULT", items: [] },
  restricted: { _id: "DEFAULT" },
};

export const getCurrentMeet = createSelector(
  [getCurrentMeetId, getMeets],
  (meetId, meets) => {
    return get(meets, meetId, fallbackMeet);
  }
);

export const getFederationConfig = createSelector([getCurrentMeet], (meet) => {
  const federationKey = get(meet, ["federation"], "");
  return get(federationConfigs, federationKey, get(federationConfigs, "OTHER"));
});

export const getIsLoading = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isLoading"],
    true
  );
export const getIsSyncing = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isSyncing"],
    false
  );
export const getIsSyncActive = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isSyncActive"],
    false
  );
export const getIsLoggedIn = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isLoggedIn"],
    false
  );
export const getIsLocal = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isLocal"],
    false
  );
export const getIsOnline = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "isOnline"],
    false
  );
export const getNotFound = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "notFound"],
    false
  );

export const getConflicts = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) =>
  get(
    getMeetMetaData(state),
    [getCurrentMeetId(state, props), "conflicts"],
    true
  );

export const getLifter = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => {
  return get(state, [
    "meets",
    getCurrentMeetId(state, props),
    "lifters",
    props.match.params.lifterId,
  ]);
};

export const getDivision = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => {
  return get(state, [
    "meets",
    getCurrentMeetId(state, props),
    "divisions",
    props.match.params.divisionId,
  ]);
};

export const getWeightClass = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => {
  return get(state, [
    "meets",
    getCurrentMeetId(state, props),
    "divisions",
    props.match.params.divisionId,
    "weightClasses",
    props.match.params.weightClassId,
  ]);
};

export const getCurrentPlatformId = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => get(props, ["match", "params", "platformId"], props.platformId);

export const getCurrentPlatform = createSelector(
  [getCurrentMeet, getCurrentPlatformId],
  (meet, currentPlatformId) => {
    if (!currentPlatformId) {
      return null;
    }
    return (
      meet.platforms?.[currentPlatformId] ??
      ({
        _id: currentPlatformId,
      } satisfies Platform)
    );
  }
);

export const getMeetLifters = createSelector([getCurrentMeet], (meet) => {
  return (meet.lifters ?? {}) satisfies Meet["lifters"];
});

export const getCurrentAttemptId = createSelector(
  [getCurrentPlatform],
  (platform) => {
    return platform?.currentAttemptId;
  }
);

export const getPlatformLifters = createSelector(
  [getMeetLifters, getCurrentPlatformId],
  (meetLifters, currentPlatformId) => {
    return filter(meetLifters, { platformId: currentPlatformId });
  }
);

export const getCurrentAttempt = createSelector(
  [getPlatformLifters, getCurrentAttemptId],
  (lifters, currentAttemptId) => {
    let currentAttempt:
      | (Attempt & { session?: number; flight?: string })
      | null = null;

    for (const lifterId in lifters) {
      for (const liftName in lifters[lifterId].lifts) {
        for (const attemptNumber in lifters[lifterId].lifts?.[
          liftName as LiftName
        ]) {
          const attempt =
            lifters[lifterId].lifts?.[liftName as LiftName]?.[
              attemptNumber as keyof LifterAttempts
            ];
          if (attempt && attempt._id === currentAttemptId) {
            currentAttempt = {
              ...attempt,
              session: lifters[lifterId].session,
              flight: lifters[lifterId].flight,
            };
            break;
          }
        }
      }
    }

    return (currentAttempt ?? {}) as
      | (Attempt & { session?: number; flight?: string })
      | {};
  }
);

export const getCurrentLifter = createSelector(
  [getPlatformLifters, getCurrentAttempt],
  (platformLifters, currentAttempt) => {
    return find(platformLifters, {
      _id: (currentAttempt as any).lifterId,
    });
  }
);

export const getCompleteLiftingOrder = createSelector(
  [getCurrentMeet, getPlatformLifters],
  (meet, platformLifters) => {
    let liftingOrder: LiftingOrder = [];
    each(platformLifters, (lifter) => {
      let liftOrder = 0;
      each(["squat", "bench", "dead"] as const, (liftName) => {
        liftOrder++;
        if (isCompetingInLift(lifter, liftName, meet as Meet)) {
          each(get(lifter, ["lifts", liftName]), (attempt) => {
            if (
              attempt &&
              attempt.weight !== 0 && //marked with a 0 to skip
              lifter.session &&
              lifter.flight
            ) {
              const info = {
                lifter: lifter,
                lot: lifter.lot,
                flight: lifter.flight as string,
                session: lifter.session as number,
                liftOrder: liftOrder as 1 | 2 | 3,
                attemptNumber: attempt.attemptNumber as AttemptNumber,
                weight: attempt.weight as number,
                attemptId: attempt._id,
                attempt: attempt,
                endOfRound: attempt.endOfRound ? (1 as const) : (0 as const),
              };

              liftingOrder.push(info);
            }
          });
        }
      });
    });

    liftingOrder = sortBy(liftingOrder, [
      "session",
      "liftOrder",
      "flight",
      "attemptNumber",
      "endOfRound",
      "weight",
      "lot",
    ]);
    return liftingOrder;
  }
);

// similar to getKnownLiftingOrder but this one includes future lifts that haven't been entered yet
export const getFutureLiftingOrder = createSelector(
  [getCurrentAttemptId, getCompleteLiftingOrder],
  (currentAttemptId, liftingOrder) => {
    const currentIndex = findIndex(
      liftingOrder,
      (a) => a.attemptId === currentAttemptId
    );
    return filter(liftingOrder, (row, i) => {
      const blankPastAttempt = i < currentIndex && !row.attempt.weight;
      return !blankPastAttempt && !row.attempt.result;
    });
  }
);

// Here we want just attempts that have been filled out. Assume 0 or blank means skip.
export const getKnownLiftingOrder = createSelector(
  [getCompleteLiftingOrder],
  (liftingOrder) => {
    return filter(liftingOrder, (row) => row.weight && !row.attempt.result);
  }
);

export const getRecordAttempts = createSelector([getCurrentMeet], (meet) => {
  const recordAttempts: Record<string, EligibleRecordWithWeight[]> = {};
  if (!meet.records) {
    return recordAttempts;
  }

  return meet.calculatedData.recordAttempts;
});

export const getRunningAttemptInputClocks = createSelector(
  [getCompleteLiftingOrder],
  (liftingOrder) => {
    return liftingOrder.filter(
      (row) => row.attempt.startTime && !row.attempt.weight
    );
  }
);

export const getScoreBoardTableData = createSelector(
  [getPlatformLifters, getCurrentAttempt, getKnownLiftingOrder],
  (platformLifters, currentAttempt, knownLiftingOrder) => {
    platformLifters = orderBy(platformLifters, [
      "session",
      "flight",
      (lifter) => {
        return get(lifter, [
          "lifts",
          (currentAttempt as any).liftName,
          (currentAttempt as any).attemptNumber,
          "endOfRound",
        ])
          ? 1
          : 0;
      },
      (lifter) => {
        // for lifters in the current flight sort by weight of current attempt.
        if (
          lifter.session === (currentAttempt as any).session &&
          lifter.flight === (currentAttempt as any).flight
        ) {
          return get(lifter, [
            "lifts",
            (currentAttempt as any).liftName,
            (currentAttempt as any).attemptNumber,
            "weight",
          ]);
        } else {
          // for lifters not in current flight sort by lifting order.
          return findIndex(
            knownLiftingOrder,
            (attempt) => get(attempt, ["lifter", "_id"]) === lifter._id
          );
        }
      },
      "lot",
    ]);

    let lastFlight: string | undefined = "";
    let lastSession: number | undefined = 0;
    const order: any = [{ row: "header" }];
    each(platformLifters, (row) => {
      if (lastSession !== row.session || lastFlight !== row.flight) {
        order.push({
          row: "title",
          name: ` — Session ${row.session ?? "MISSING"} - Flight ${
            row.flight ?? "MISSING"
          }`,
        });
      }

      order.push(row);
      lastFlight = row.flight;
      lastSession = row.session;
    });

    return order;
  }
);

export const getLeftRefDoc = createSelector(
  [getCurrentPlatform],
  (currentPlatform) => {
    return currentPlatform?.refs?.left;
  }
);

export const getHeadRefDoc = createSelector(
  [getCurrentPlatform],
  (currentPlatform) => {
    return currentPlatform?.refs?.head;
  }
);

export const getRightRefDoc = createSelector(
  [getCurrentPlatform],
  (currentPlatform) => {
    return currentPlatform?.refs?.right;
  }
);

export const getCurrentRefPosition = (
  state: ReduxState,
  props: ReactRouterWithRouterProps
) => get(props, ["match", "params", "position"]) as RefPosition;

export const getCurrentRefDoc = createSelector(
  [getCurrentMeet, getCurrentPlatform, getCurrentRefPosition],
  (meet, currentPlatform, position) => {
    if (!position) {
      return null;
    }

    return currentPlatform?.refs?.[position] ?? null;
  }
);
